// src/lib/menage-frise-api.js
//
// ÉCRITURES DE LA FRISE — l'ordre fixé, les exceptions de durée, les
// interventions manuelles. Migration `20261122000001` appliquée le 2026-08-10.
//
// PAS D'ENDPOINT SERVEUR ICI, et c'est un choix assumé, le même que pour les
// règles d'assignation : la sécurité est DANS LA BASE. `menage_runs` porte la
// policy `orchestrateur_assign` (pool ∧ logIds ∧ statut ∧ verrou 48 h) DOUBLÉE
// du trigger `enforce_menage_runs_colonnes` qui n'ouvre `heure_fixee` /
// `duree_min` qu'au rôle orchestrateur ; `menage_interventions` porte ses
// propres policies et un trigger qui force la traçabilité depuis le jeton.
// Un contrôleur applicatif n'ajouterait rien qu'on ne puisse contourner.
//
// ⚠️ ROWCOUNT-CHECK SYSTÉMATIQUE. Une RLS qui refuse ne lève PAS d'erreur : elle
// renvoie ZÉRO ligne, en silence. Sans `.select()` et sans compter ce qui
// revient, un refus se lit exactement comme un succès — et l'écran afficherait
// un ordre que la base n'a jamais accepté. C'est le même piège que la
// réassignation (handleReassign), et il se referme de la même façon.

// ⚠️ `terminee_par` / `terminee_le` : PostgREST ne rend QUE les colonnes du
// select — la même famille de bug que la trace « dernière main » (2026-08-13),
// attrapée ici AVANT la prod cette fois.
const COLS_INTERVENTION =
  'id, logement_id, scheduled_date, libelle, duree_min, heure_fixee, ' +
  'assigned_provider_email, statut, creee_par, creee_par_role, creee_le, ' +
  'terminee_par, terminee_le'
// La VUE ajoute `personnel` ; la TABLE (repli + retours d'écriture) aussi,
// mais seulement une fois la migration 20261203000001 appliquée — les
// écritures gardent donc COLS_INTERVENTION, sans `personnel`, jusque-là.
const COLS_INTERVENTION_VUE = COLS_INTERVENTION + ', personnel'

/** "HH:MM" (ou "HH:MM:SS") tel que Postgres le rend → "HH:MM" pour l'écran. */
export function heureCourte(valeur) {
  if (typeof valeur !== 'string') return null
  const m = valeur.match(/^(\d{2}):(\d{2})/)
  return m ? `${m[1]}:${m[2]}` : null
}

/** Message unique des refus — l'utilisateur n'a pas à deviner la cause exacte. */
const REFUS =
  "Refusé : hors de votre périmètre, hors pool, ménage terminé ou trop ancien."

/**
 * Fixe l'heure de début d'un ménage (glisser-déposer).
 *
 * @returns {Promise<{ok: boolean, erreur: string|null}>}
 */
export async function fixerHeureRun(supabase, runId, heureHHMM) {
  try {
    const { data, error } = await supabase
      .from('menage_runs')
      .update({ heure_fixee: heureHHMM })
      .eq('id', runId)
      .select('id')
    if (error || !data || data.length === 0) {
      if (error) console.error('[frise] fixerHeureRun', error.message || error)
      return { ok: false, erreur: REFUS }
    }
    return { ok: true, erreur: null }
  } catch (e) {
    console.error('[frise] fixerHeureRun', e?.message || e)
    return { ok: false, erreur: REFUS }
  }
}

/**
 * Pose l'exception de durée d'un ménage.
 *
 * `null` REMET la règle du logement — ce n'est pas « effacer », c'est revenir au
 * défaut, et l'appelant doit pouvoir le faire sans supprimer la ligne.
 */
export async function fixerDureeRun(supabase, runId, minutes) {
  try {
    const { data, error } = await supabase
      .from('menage_runs')
      .update({ duree_min: minutes == null ? null : Number(minutes) })
      .eq('id', runId)
      .select('id')
    if (error || !data || data.length === 0) {
      if (error) console.error('[frise] fixerDureeRun', error.message || error)
      return { ok: false, erreur: REFUS }
    }
    return { ok: true, erreur: null }
  } catch (e) {
    console.error('[frise] fixerDureeRun', e?.message || e)
    return { ok: false, erreur: REFUS }
  }
}

/**
 * Interventions d'une fenêtre de dates. La RLS borne déjà le périmètre —
 * aucun filtre client à ajouter, et surtout aucun qu'on prendrait ensuite pour
 * un droit.
 */
export async function chargerInterventions(supabase, { du, au }) {
  // ⚠️ LA LECTURE PASSE PAR LA VUE `menage_interventions_visibles`
  // (arbitrage Dimitri 2026-08-17) : une intervention PERSONNELLE arrive
  // chez l'admin SANS libellé, SANS lieu, SANS créateur — blanchie CÔTÉ
  // SERVEUR, jamais à l'écran seulement. Agent + heures restent servis :
  // le créneau doit occuper le couloir et compter dans les conflits.
  // REPLI INERTE-COMPATIBLE : tant que la migration 20261203000001 n'est
  // pas appliquée, la vue n'existe pas (42P01) → on relit la table, comme
  // avant (et `personnel` n'existe pas encore = aucune personnelle en base).
  let { data, error } = await supabase
    .from('menage_interventions_visibles')
    .select(COLS_INTERVENTION_VUE)
    .gte('scheduled_date', du)
    .lte('scheduled_date', au)
  if (error) {
    ;({ data, error } = await supabase
      .from('menage_interventions')
      .select(COLS_INTERVENTION)
      .gte('scheduled_date', du)
      .lte('scheduled_date', au))
  }
  if (error) {
    console.error('[frise] chargerInterventions', error.message || error)
    return { interventions: [], erreur: error.message }
  }
  return {
    interventions: (data || []).map((it) => ({ ...it, heure_fixee: heureCourte(it.heure_fixee) })),
    erreur: null,
  }
}

/**
 * Crée une intervention.
 *
 * On n'envoie JAMAIS `creee_par` / `creee_par_role` : le trigger les écrase
 * depuis le jeton. Les envoyer donnerait l'illusion qu'ils sont pilotables
 * côté client, et un jour quelqu'un s'appuierait dessus.
 */
export async function creerIntervention(supabase, { logement_id, scheduled_date, libelle, duree_min, assigned_provider_email, heure_fixee, personnel }) {
  try {
    const { data, error } = await supabase
      .from('menage_interventions')
      .insert({
        logement_id,
        scheduled_date,
        libelle: String(libelle || '').trim(),
        duree_min: Number(duree_min) || 90,
        assigned_provider_email: assigned_provider_email || null,
        heure_fixee: heure_fixee || null,
        // La case « Personnel » (orchestrateur seul à l'écran) — absente ou
        // false : intervention ordinaire, rien ne change.
        ...(personnel === true ? { personnel: true } : {}),
      })
      .select(COLS_INTERVENTION)
    if (error || !data || data.length === 0) {
      if (error) console.error('[frise] creerIntervention', error.message || error)
      return { ok: false, intervention: null, erreur: REFUS }
    }
    const it = data[0]
    return { ok: true, intervention: { ...it, heure_fixee: heureCourte(it.heure_fixee) }, erreur: null }
  } catch (e) {
    console.error('[frise] creerIntervention', e?.message || e)
    return { ok: false, intervention: null, erreur: REFUS }
  }
}

/**
 * Met à jour une intervention — heure, durée, agent.
 *
 * Un seul point d'écriture pour les trois : les gestes de la frise (déplacer,
 * étirer, changer d'agent) touchent les mêmes lignes, et trois fonctions
 * quasi identiques finiraient par diverger sur le rowcount-check.
 */
export async function majIntervention(supabase, id, champs) {
  try {
    const { data, error } = await supabase
      .from('menage_interventions')
      .update(champs)
      .eq('id', id)
      .select('id')
    if (error || !data || data.length === 0) {
      if (error) console.error('[frise] majIntervention', error.message || error)
      return { ok: false, erreur: REFUS }
    }
    return { ok: true, erreur: null }
  } catch (e) {
    console.error('[frise] majIntervention', e?.message || e)
    return { ok: false, erreur: REFUS }
  }
}

/** Compat : l'heure seule reste un cas courant. */
export async function fixerHeureIntervention(supabase, id, heureHHMM) {
  return majIntervention(supabase, id, { heure_fixee: heureHHMM })
}

/**
 * L'AGENT MARQUE UNE INTERVENTION FAITE — le seul geste d'écriture qui lui soit
 * ouvert sur cette table (migration 20261123000001).
 *
 * ⚠️ ELLE VIT ICI, avec les autres écritures de la table, pour la raison déjà
 * écrite plus haut : trois fonctions quasi identiques réparties dans deux
 * fichiers finiraient par diverger sur le rowcount-check, et c'est lui qui
 * distingue « refusé par la base » de « accepté ». La base est de toute façon
 * la seule autorité — la policy `interventions_menage_update` et le trigger de
 * colonnes bornent ce geste, pas cette fonction.
 *
 * `statut` est le SEUL champ envoyé : `terminee_par` / `terminee_le` sont posés
 * par le trigger depuis le jeton. Les envoyer donnerait l'illusion qu'ils sont
 * pilotables côté client.
 */
export async function validerIntervention(supabase, id) {
  try {
    const { data, error } = await supabase
      .from('menage_interventions')
      .update({ statut: 'completed' })
      .eq('id', id)
      .select('id, statut, terminee_par, terminee_le')
    if (error || !data || data.length === 0) {
      if (error) console.error('[interventions] validerIntervention', error.message || error)
      return { ok: false, intervention: null, erreur: REFUS }
    }
    return { ok: true, intervention: data[0], erreur: null }
  } catch (e) {
    console.error('[interventions] validerIntervention', e?.message || e)
    return { ok: false, intervention: null, erreur: REFUS }
  }
}

/**
 * ACQUITTER UN RECALAGE — « C'est bon, je l'ai vu ».
 *
 * ⚠️ ON ENVOIE L'HEURE, PAS UN BOOLÉEN. C'est elle qui fait taire le motif, et
 * seulement tant que le recalage vaut cette heure-là : un recalage FUTUR et
 * DIFFÉRENT rallume la ligne. Un simple « accepté » l'aurait éteinte pour
 * toujours, y compris pour un décalage que personne n'aurait jamais vu.
 *
 * `recalage_accepte_par` / `_le` ne sont JAMAIS envoyés : le trigger
 * `zzy_menage_runs_recalage_trace` les pose depuis le jeton.
 */
export async function accepterRecalage(supabase, id, minutes) {
  try {
    const m = Number(minutes)
    if (!Number.isFinite(m) || m < 0 || m >= 1440) {
      return { ok: false, erreur: REFUS }
    }
    const { data, error } = await supabase
      .from('menage_runs')
      .update({ recalage_accepte_a: m })
      .eq('id', id)
      .select('id')
    if (error || !data || data.length === 0) {
      if (error) console.error('[frise] accepterRecalage', error.message || error)
      return { ok: false, erreur: REFUS }
    }
    return { ok: true, erreur: null }
  } catch (e) {
    console.error('[frise] accepterRecalage', e?.message || e)
    return { ok: false, erreur: REFUS }
  }
}

/** Supprime une intervention (elle n'a pas eu lieu / posée par erreur). */
export async function supprimerIntervention(supabase, id) {
  try {
    const { data, error } = await supabase
      .from('menage_interventions')
      .delete()
      .eq('id', id)
      .select('id')
    if (error || !data || data.length === 0) {
      if (error) console.error('[frise] supprimerIntervention', error.message || error)
      return { ok: false, erreur: REFUS }
    }
    return { ok: true, erreur: null }
  } catch (e) {
    console.error('[frise] supprimerIntervention', e?.message || e)
    return { ok: false, erreur: REFUS }
  }
}

export const _REFUS = REFUS
